// src/hooks/useTesisatMeshSync.js
//
// Tesisat (priz, panel, makine, vs.) ve wire ekleme/değişimini dinler;
// scene'deki ilgili mesh'leri fpMesh-child yapar (mesh-FP mimarisi).
//
// useElectricalLayout effect'i sadece layout dependency'leri değiştiğinde
// tetiklenir — tesisat ekleme bunları doğrudan değiştirmez. Bu yüzden
// sweep çalışmıyor, tesisat scene root'ta kalıyor → mesh ile hareket
// etmiyor.
//
// Bu hook tesisat/wire slice'larını dinler, değişiklik olduğunda küçük
// bir gecikme ile (sahnede mesh yerleşene kadar) sweep çağırır.
//
// Mount: DrawingCanvas.jsx içinde scene oluştuktan sonra.

import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as THREE from 'three';
import { sweepTesisatToFloorPlanMeshes } from '../utils/floorPlanMesh';
import { getActiveFloorPlanId, getGlobalDocumentBounds } from '../../../utils/getActiveFloorPlanId';
import { selectAllTesisat, updateTesisat } from '../../../redux/tesisatSlice';
import { worldToLocal } from '../../../shared/coordinates/floorPlanOrigin';
import { isWithinSnapshotWindow } from '../../persistence';

const LOCAL_BUG_TYPES = ['socket', 'mainPanel', 'electricalPanel', 'supplyPoint', 'machine', 'LightSwitch', 'LightFixture', 'cableShaft'];

export default function useTesisatMeshSync(scenesRef, tabId) {
  // Tesisat slice byId — id seti değiştiğinde tetiklenir
  const tesisatById = useSelector((s) => s.tesisat?.byId);
  const wiresById = useSelector((s) => s.wires?.byId);
  const items = useSelector(selectAllTesisat);
  const dispatch = useDispatch();
  // items'i ref ile tut — migration icinde kullanilir ama dependency olarak
  // eklenmez (cunku migration dispatch → items degisir → sonsuz re-trigger)
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    // [SAVE-LOAD-REWRITE] Snapshot pencere içinde skip — drawing-time'da çalışır
    if (isWithinSnapshotWindow()) return;

    const scene = scenesRef?.current?.[tabId];
    if (!scene) return;
    const id = setTimeout(() => {
      try {
        const bounds = getGlobalDocumentBounds();
        const result = sweepTesisatToFloorPlanMeshes(scene, (pos) =>
          getActiveFloorPlanId(pos, bounds)
        );
        if (result.attached > 0) {
          const needsMigration = itemsRef.current.filter(
            i => !i._v && i.floorPlanId && LOCAL_BUG_TYPES.includes(i.type)
          );
          for (const item of needsMigration) {
            let mesh = null;
            scene.traverse(obj => {
              if (!mesh && obj.userData?.id === item.id) mesh = obj;
            });
            if (!mesh) continue;
            let inFpMesh = false;
            let p = mesh.parent;
            while (p && p !== scene) {
              if (p.userData?.isFloorPlanMesh) { inFpMesh = true; break; }
              p = p.parent;
            }
            if (!inFpMesh) continue;
            const wp = new THREE.Vector3();
            mesh.getWorldPosition(wp);
            dispatch(updateTesisat({
              id: item.id,
              changes: {
                position: { x: wp.x, y: wp.y, z: wp.z },
                _v: 2,
                localPosition: worldToLocal({ x: wp.x, y: wp.y }, item.floorPlanId, scene),
              },
            }));
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[tesisat-sync] hata:', err);
      }
    }, 50);
    return () => clearTimeout(id);
  }, [scenesRef, tabId, tesisatById, wiresById, dispatch]);
}
